import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  SimplefinBackfillInput,
  SimplefinConnectInput,
  SimplefinStatus,
  SimplefinSyncResult,
} from "@money/shared";
import { apiFetch } from "./client";
import { accountsKey } from "./accounts";

export const simplefinStatusKey = ["simplefin", "status"] as const;

export function useSimplefinStatus() {
  return useQuery({
    queryKey: simplefinStatusKey,
    queryFn: () => apiFetch<SimplefinStatus>("/simplefin/status"),
    staleTime: 30_000,
  });
}

export function useSimplefinConnect() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SimplefinConnectInput) =>
      apiFetch<SimplefinStatus>("/simplefin/connect", { method: "POST", body: input }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: simplefinStatusKey });
    },
  });
}

export function useSimplefinSync() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<SimplefinSyncResult>("/simplefin/sync", { method: "POST" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: simplefinStatusKey });
      qc.invalidateQueries({ queryKey: accountsKey });
      qc.invalidateQueries({ queryKey: ["transactions"] });
    },
  });
}

export function useSimplefinBackfill() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SimplefinBackfillInput) =>
      apiFetch<SimplefinSyncResult>("/simplefin/backfill", { method: "POST", body: input }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: simplefinStatusKey });
      qc.invalidateQueries({ queryKey: accountsKey });
      qc.invalidateQueries({ queryKey: ["transactions"] });
    },
  });
}

export function useSimplefinDisconnect() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiFetch<void>("/simplefin/disconnect", { method: "POST" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: simplefinStatusKey });
    },
  });
}
